import { useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '@/store/auth'
import { pinsKey } from '@/lib/bulkDelete'
import { Pin } from 'lucide-react'
import { socialApi } from '@/lib/socialApi'
import { describeError } from '@/lib/auth'
import { formatDateTime } from '@/lib/format'
import { renderMarkdown } from '@/lib/markdown'
import { mentionNames } from '@/lib/mentions'
import { displayName } from '@/store/social'
import { cx } from '@/lib/cx'
import styles from './PinnedPanel.module.css'

/** Всплывающий список закреплённых сообщений канала; клик — переход к сообщению. */
export function PinnedPanel({ channelId, onJump, onClose }: { channelId: string; onJump: (messageId: string) => void; onClose: () => void }) {
  const userId = useAuthStore((s) => s.user?.id)
  const query = useQuery({ queryKey: pinsKey(userId, channelId), queryFn: () => socialApi.pins(channelId), enabled: Boolean(userId), staleTime: 0 })
  const pins = query.data
  const error = query.error ? describeError(query.error).message : null
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose()
    }
    document.addEventListener('mousedown', onDown)
    return () => {
      document.removeEventListener('mousedown', onDown)
    }
  }, [channelId, onClose])

  return (
    <div className={cx(styles.panel, 'klichat-popover')} ref={ref} role="dialog" aria-label="Закреплённые сообщения">
      <div className={styles.title}><Pin size={16} /> Закреплённые сообщения</div>
      <div className={cx(styles.list, 'scroller')}>
        {error ? <p className={styles.empty}>{error}</p> : null}
        {pins && pins.length === 0 ? <p className={styles.empty}>Пока ничего не закреплено. Закрепить можно из меню сообщения.</p> : null}
        {!pins && !error ? <p className={styles.empty}>Загружаем…</p> : null}
        {pins?.map((m) => (
          <button key={m.id} type="button" className={styles.item} onClick={() => { onJump(m.id); onClose() }}>
            <span className={styles.author}>{displayName(m.author)} <time className={styles.time}>{formatDateTime(new Date(m.timestamp))}</time></span>
            <span className={styles.text}>{m.content ? renderMarkdown(m.content, { userName: mentionNames(m), inline: true }) : m.attachments?.length ? `📎 ${m.attachments[0]?.filename ?? ''}` : '…'}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
